import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import dayjs from 'dayjs';
import Litepicker from '@/components/Base/Litepicker';
import Lucide from '@/components/Base/Lucide';
import Button from '@/components/Base/Button';
import Table from '@/components/Base/Table';
import { fetchQrTracking, type QrTrackingData } from '@/api/qrTracking';

const QR_TRACKING_DATE_RANGE_KEY = 'ldsurvey:admin:qrTrackingDateRange';

function todayMYT() {
  return dayjs().format('YYYY-MM-DD');
}

function todayISORange() {
  const today = dayjs().format('YYYY-MM-DD');
  return {
    startISO: dayjs(today).startOf('day').toISOString(),
    endISO: dayjs(today).endOf('day').toISOString(),
  };
}

function toIsoRange(range: string) {
  const raw = (range || '').trim();
  if (!raw) return null;
  const [startRaw, endRaw] = raw.split(' - ');
  const startStr = (startRaw || '').trim();
  const endStr = ((endRaw || '') || startStr).trim();
  if (!startStr) return null;
  const start = dayjs(startStr);
  const end = dayjs(endStr);
  if (!start.isValid() || !end.isValid()) return null;
  return {
    startISO: start.startOf('day').toISOString(),
    endISO: end.endOf('day').toISOString(),
  };
}

function conversionTone(rate: number, qrScans: number): string {
  if (qrScans <= 0) return 'text-slate-400';
  if (rate >= 50) return 'text-emerald-600';
  if (rate >= 25) return 'text-amber-600';
  return 'text-rose-600';
}

export default function QrTracking() {
  const readInitialDateRange = () => {
    if (typeof window === 'undefined') return `${todayMYT()} - ${todayMYT()}`;
    const saved = window.localStorage.getItem(QR_TRACKING_DATE_RANGE_KEY);
    return saved?.trim() || `${todayMYT()} - ${todayMYT()}`;
  };

  const [dateRange, setDateRange] = useState(readInitialDateRange);
  const [confirmedRange, setConfirmedRange] = useState(
    () => toIsoRange(readInitialDateRange()) ?? todayISORange()
  );
  const [data, setData] = useState<QrTrackingData | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const value = dateRange.trim();
    if (value) window.localStorage.setItem(QR_TRACKING_DATE_RANGE_KEY, value);
  }, [dateRange]);

  const loadData = useCallback(async () => {
    if (!confirmedRange) return;
    setLoading(true);
    setError('');
    try {
      const result = await fetchQrTracking({
        startISO: confirmedRange.startISO,
        endISO: confirmedRange.endISO,
      });
      setData(result);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load QR tracking');
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [confirmedRange]);

  useEffect(() => {
    void loadData();
  }, [loadData]);

  const handleSearch = () => {
    const iso = toIsoRange(dateRange);
    if (!iso) {
      setError('Please select a valid date range');
      return;
    }
    setConfirmedRange(iso);
  };

  const summary = data?.summary;
  const ambassadors = data?.ambassadors ?? [];

  return (
    <div className="p-6 space-y-5">
      <div className="box rounded-xl border border-slate-200/60 dark:border-darkmode-400 px-6 py-5 shadow-sm">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
          <div>
            <h2 className="text-xl font-semibold text-slate-800 dark:text-slate-100">
              QR Tracking
            </h2>
          </div>
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
            <div className="relative w-full sm:min-w-[280px]">
              <Lucide
                icon="Calendar"
                className="absolute left-3 top-1/2 z-10 h-4 w-4 -translate-y-1/2 text-slate-400 pointer-events-none"
              />
              <Litepicker
                value={dateRange}
                onChange={(e) => setDateRange(e.target.value)}
                options={{
                  format: 'YYYY-MM-DD',
                  singleMode: false,
                  numberOfColumns: 2,
                  numberOfMonths: 2,
                  autoApply: false,
                }}
                className="w-full rounded-lg border border-slate-200/80 bg-white py-2.5 pl-10 pr-4 text-sm dark:border-darkmode-400 dark:bg-darkmode-600"
              />
            </div>
            <Button type="button" variant="primary" onClick={handleSearch} disabled={loading}>
              {loading ? (
                <>
                  <Lucide icon="Loader" className="stroke-[1.3] w-4 h-4 mr-2 animate-spin" />
                  Loading…
                </>
              ) : (
                <>
                  <Lucide icon="Search" className="stroke-[1.3] w-4 h-4 mr-2" />
                  Search
                </>
              )}
            </Button>
          </div>
        </div>
      </div>

      {error && (
        <div className="px-4 py-3 rounded-lg bg-danger/10 text-danger text-sm border border-danger/20">
          {error}
        </div>
      )}

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <div className="box rounded-xl border border-slate-200/60 px-6 py-5 shadow-sm dark:border-darkmode-400">
          <p className="text-xs font-medium uppercase tracking-wide text-slate-500">
            Total QR Scans
          </p>
          <p className="mt-2 text-3xl font-bold tabular-nums text-slate-800 dark:text-slate-100">
            {loading && !data ? (
              <span className="text-xl text-slate-400 animate-pulse">…</span>
            ) : (
              (summary?.totalQrScans ?? 0).toLocaleString()
            )}
          </p>
          <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
            Times consumers opened the survey via ambassador QR links
          </p>
        </div>

        <div className="box rounded-xl border border-slate-200/60 px-6 py-5 shadow-sm dark:border-darkmode-400">
          <p className="text-xs font-medium uppercase tracking-wide text-slate-500">
            Survey Completion Rate
          </p>
          <p className="mt-2 text-3xl font-bold tabular-nums text-slate-800 dark:text-slate-100">
            {loading && !data ? (
              <span className="text-xl text-slate-400 animate-pulse">…</span>
            ) : (
              `${summary?.surveyCompletionRate ?? 0}%`
            )}
          </p>
          <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
            {(summary?.totalCompletedSurveys ?? 0).toLocaleString()} completed surveys from{' '}
            {(summary?.totalQrScans ?? 0).toLocaleString()} QR scans
          </p>
        </div>
      </div>

      <div className="box rounded-xl border border-slate-200/60 dark:border-darkmode-400 shadow-sm overflow-hidden">
        <div className="px-6 py-4 border-b border-slate-200/80 dark:border-darkmode-400">
          <h3 className="text-base font-semibold text-slate-800 dark:text-slate-100">
            Ambassador Conversion Analysis
          </h3>
        </div>

        <div className="overflow-x-auto">
          <Table className="border-separate border-spacing-0">
            <Table.Thead>
              <Table.Tr>
                {['Ambassador', 'QR Scans', 'Completed Surveys', 'Conversion Rate'].map(
                  (label) => (
                    <Table.Th
                      key={label}
                      className="whitespace-nowrap px-4 py-3 text-xs font-semibold uppercase tracking-wide text-slate-500"
                    >
                      {label}
                    </Table.Th>
                  )
                )}
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {loading && !data ? (
                <Table.Tr>
                  <Table.Td colSpan={4} className="px-6 py-12 text-center text-slate-400">
                    <Lucide icon="Loader" className="w-5 h-5 mx-auto mb-2 animate-spin" />
                    Loading ambassador data…
                  </Table.Td>
                </Table.Tr>
              ) : ambassadors.length === 0 ? (
                <Table.Tr>
                  <Table.Td colSpan={4} className="px-6 py-12 text-center text-slate-400">
                    No ambassador QR activity in the selected date range
                  </Table.Td>
                </Table.Tr>
              ) : (
                ambassadors.map((row) => (
                  <Table.Tr
                    key={row.staffId}
                    className="border-t border-slate-100 dark:border-darkmode-400"
                  >
                    <Table.Td className="px-4 py-3">
                      <Link
                        to={`/ambassador/${row.staffId}`}
                        className="text-sm font-medium text-primary hover:underline"
                      >
                        {row.name}
                      </Link>
                      {row.memberCode && (
                        <div className="text-xs text-slate-500">{row.memberCode}</div>
                      )}
                    </Table.Td>
                    <Table.Td className="px-4 py-3 text-sm tabular-nums">
                      {row.qrScans.toLocaleString()}
                    </Table.Td>
                    <Table.Td className="px-4 py-3 text-sm tabular-nums">
                      {row.completedSurveys.toLocaleString()}
                    </Table.Td>
                    <Table.Td className="px-4 py-3">
                      <span
                        className={`text-sm font-semibold tabular-nums ${conversionTone(row.conversionRatePct, row.qrScans)}`}
                      >
                        {row.qrScans > 0 ? `${row.conversionRatePct}%` : '—'}
                      </span>
                    </Table.Td>
                  </Table.Tr>
                ))
              )}
            </Table.Tbody>
          </Table>
        </div>
      </div>
    </div>
  );
}
